import { useEffect, useRef } from "react";
import { ExternalLink, Calendar, Rocket } from "lucide-react";
import { REEL_CATEGORIES } from "@/lib/content";
import { Reveal, Eyebrow, GhostButton } from "@/components/site/ui";
import { ReelGrid } from "@/components/site/ReelGrid";

function HtmlEmbed({ html }) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.innerHTML = html;
    // Re-inject script tags so embed widgets (SnapWidget, Behold, etc.) execute.
    el.querySelectorAll("script").forEach((old) => {
      const s = document.createElement("script");
      [...old.attributes].forEach((a) => s.setAttribute(a.name, a.value));
      s.text = old.text;
      old.replaceWith(s);
    });
  }, [html]);
  return <div ref={ref} data-testid="instagram-feed-embed" className="ig-embed" />;
}

export function Journey({ embeds, results, settings }) {
  const list = results || [];
  const latest = list.filter((r) => r.kind !== "upcoming");
  const upcoming = list.filter((r) => r.kind === "upcoming");
  const igCode = settings?.instagram_embed_code?.trim();

  return (
    <section id="journey" className="py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="text-center max-w-2xl mx-auto">
          <Reveal><Eyebrow>The Journey</Eyebrow></Reveal>
          <Reveal delay={0.05}><h2 className="mt-4 font-heading text-4xl md:text-6xl text-ink">Building The Dream</h2></Reveal>
          <Reveal delay={0.1}><p className="mt-5 font-accent text-2xl md:text-3xl italic text-bronze">One season. One race. One lesson at a time.</p></Reveal>
        </div>

        <div className="mt-14 grid lg:grid-cols-2 gap-8">
          <Reveal>
            <div>
              <Eyebrow>Latest Results</Eyebrow>
              <div className="mt-5 space-y-3" data-testid="journey-results">
                {latest.map((r) => (
                  <div key={r.id} className="rounded-2xl bg-ivory border border-champagne/30 p-5">
                    <div className="flex items-start justify-between gap-4">
                      <div><p className="font-heading text-lg text-ink">{r.event}</p><p className="font-body text-sm text-ink/60">{r.meet}</p></div>
                      <div className="text-right shrink-0"><p className="font-heading text-base text-bronze">{r.result}</p><p className="label-eyebrow">{r.date}</p></div>
                    </div>
                    {r.link && (
                      <a href={r.link} target="_blank" rel="noopener noreferrer" data-testid={`result-link-${r.id}`} className="mt-3 inline-flex items-center gap-1 font-body text-xs uppercase tracking-wide text-bronze hover:text-ink transition-colors">
                        See Results <ExternalLink size={12} />
                      </a>
                    )}
                  </div>
                ))}
                {latest.length === 0 && <p className="font-body text-sm text-ink/50">Results coming soon.</p>}
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <div>
              <Eyebrow>Upcoming</Eyebrow>
              <div className="mt-5 space-y-3" data-testid="journey-upcoming">
                {upcoming.map((u) => (
                  <div key={u.id} className="rounded-2xl bg-gradient-to-br from-blush/25 to-champagne/10 border border-champagne/30 p-5 flex items-center gap-4">
                    <Calendar className="text-bronze shrink-0" size={20} />
                    <div>
                      <p className="font-heading text-lg text-ink">{u.event}</p>
                      <p className="font-body text-sm text-ink/60">{u.meet} · {u.date}</p>
                      {u.link && (
                        <a href={u.link} target="_blank" rel="noopener noreferrer" data-testid={`result-link-${u.id}`} className="mt-1.5 inline-flex items-center gap-1 font-body text-xs uppercase tracking-wide text-bronze hover:text-ink transition-colors">
                          Event Details <ExternalLink size={12} />
                        </a>
                      )}
                    </div>
                  </div>
                ))}
                {upcoming.length === 0 && <p className="font-body text-sm text-ink/50">Schedule to be announced.</p>}
              </div>
              <div className="mt-6 rounded-[2rem] bg-gradient-to-br from-blush/30 to-ivory border border-champagne/30 p-8">
                <Rocket className="text-bronze" size={24} />
                <h3 className="mt-4 font-heading text-2xl text-ink">Road to Sub-51</h3>
                <p className="mt-3 font-body text-sm text-ink/75 leading-relaxed">The next milestone is breaking the 51-second barrier in the open 400m. Follow every workout, meet, and lesson along the way.</p>
                <div className="mt-4 flex items-end gap-3"><span className="font-heading text-4xl text-bronze">51.54</span><span className="font-body text-xs text-ink/60 mb-1.5">current PR · chasing 50.xx</span></div>
              </div>
            </div>
          </Reveal>
        </div>

        <div className="mt-16 text-center max-w-2xl mx-auto">
          <Eyebrow>Latest From Leah</Eyebrow>
          <h3 className="mt-3 font-heading text-3xl md:text-5xl text-ink">Follow The Journey</h3>
          <p className="mt-4 font-accent text-2xl italic text-bronze">A front-row seat to the life of a professional athlete.</p>
        </div>
        <div className="mt-10">
          {igCode ? <HtmlEmbed html={igCode} /> : <ReelGrid embeds={(embeds || []).slice(0, 6)} categories={REEL_CATEGORIES} />}
        </div>
        <div className="mt-10 text-center"><GhostButton href="https://instagram.com/leleskye_" testid="latest-instagram-btn">Follow @leleskye_</GhostButton></div>
      </div>
    </section>
  );
}
